import { createMessage, SB } from '@play-co/replicant';
import createMessages from '../config/messages/createMessages';
import { isExistingJankenResult } from './janken.getters';
import { createJankenPendingResult } from './janken.modifiers';
import { parseJankenChallengeTimestamp } from './janken.utils';

export const jankenMessages = createMessages({
  /**
   * Cross-user postMessage sent by the receiver's `resolveJankenChallenge` action
   * to notify the challenge sender that their challenge was played.
   *
   * Runs on the **sender's** state. The message context `senderId` is the friend
   * who resolved the challenge, aliased as `friendId` in the handler.
   *
   * Flow:
   *   Friend taps challenge link
   *   → `resolveJankenChallenge` action runs on friend's state
   *   → action calls `api.postMessage.jankenChallengeResolved(senderId, { challengeId })`
   *   → this handler runs on sender's state
   *   → stores a pending result for the sender to view on next login
   *
   * Deduplicates by `challengeId + friendId` — a second postMessage for the
   * same pair is silently ignored.
   *
   * @example
   * // Sent from the resolveJankenChallenge action:
   * api.postMessage.jankenChallengeResolved(senderId, {
   *   challengeId: 'jnk_1708300000000_482917365',
   * });
   */
  jankenChallengeResolved: createMessage(
    SB.object({
      challengeId: SB.string(),
      opponentSpecies: SB.string().optional(),
      opponentDisplayName: SB.string().optional(),
    }),
    (
      state,
      { challengeId, opponentSpecies, opponentDisplayName },
      { senderId: friendId, timestamp },
    ) => {
      // Reject malformed challenge IDs (wrong prefix, missing timestamp, etc.)
      if (!parseJankenChallengeTimestamp(challengeId)) {
        return;
      }

      // Dedup — already have a pending result for this challengeId+friendId pair
      if (isExistingJankenResult(state, challengeId, friendId)) {
        return;
      }

      // Store pending result for sender to view on next login
      createJankenPendingResult(state, {
        challengeId,
        receiverId: friendId,
        createdAt: timestamp,
        opponentSpecies,
        opponentDisplayName,
      });
    },
  ),
});
